import { InversifyHelpers } from "configuration/inversify/container/helpers";
import componentModel from "decorators/componentModel";
import { wccModules } from "enums/wccModules";
import { inject, injectable } from "inversify";
import { Subscribable } from "knockout";
import { NodeTracker } from "managers/nodes/tracker";
import { EffectsContainer } from "mixins/withEffects";
import { IWCCSliderAnimator } from "./animators/animator.interfaces";
import { WCCSliderAlignment } from "./enums/alignment";
import { WCCSliderItemState } from "./enums/itemState";
import WCCSliderAnimatorsFactory from "./factories/animators";
import { IWCCSliderAnimatorsFactory, wccSliderAnimatorsFactoryKey } from "./factories/animators.interfaces";
import WCCSliderLayoutsFactory from "./factories/layout";
import { IWCCSliderLayoutsFactory, wccSliderLayoutsFactoryKey } from "./factories/layout.interfaces";
import WCCSliderItem from "./item/base/base.model";
import { WCCSliderBaseLayoutConfig } from "./layouts/base";
import { IWCCSliderLayout } from "./layouts/layout.interfaces";
import WCCSliderAutoScrollManager from "./managers/autoScroll";
import WCCSliderResizer from "./managers/resizer";
import WCCSliderPage from "./models/page";
import './slider.html';
import { WCCSlider } from "./slider.model";
import './slider.scss';

components
    .registerLocal('wcc-slider-item-adaptive', () => require('components/slider/item/adaptive/adaptive'))
    .registerLocal('wcc-slider-item-dynamic', () => require('components/slider/item/dynamic/dynamic'))
    .registerLocal('wcc-slider-item-fullscreen', () => require('components/slider/item/fullscreen/fullscreen'));

InversifyHelpers
    .registerInjectable(container => container.bind<IWCCSliderAnimatorsFactory>(wccSliderAnimatorsFactoryKey).to(WCCSliderAnimatorsFactory))
    .registerInjectable(container => container.bind<IWCCSliderLayoutsFactory>(wccSliderLayoutsFactoryKey).to(WCCSliderLayoutsFactory));

const renderStates = [
    WCCSliderItemState.pending,
    WCCSliderItemState.active
]

interface WCCSliderTemplates {
    item: Node[]
    controlNext: Node[]
    controlPrev: Node[]
}

@injectable()
@componentModel(WCCSlider)
export default class WCCSliderVM<T> {
    private animator = ko.observable<IWCCSliderAnimator>();
    private layout = ko.observable<IWCCSliderLayout<T>>();

    bodyNode = ko.observable<HTMLElement>();

    selectedSourceItem = ko.observable<T>();

    shift = ko.observable(0);
    trackHeight = ko.observable<number>();

    isMouseInsideSlider = ko.observable(false);

    templates: WCCSliderTemplates
    
    width: Subscribable<number>
    height: Subscribable<number | undefined>
    spaceBetweenPages: Subscribable<number>
    otherPageElementSize: Subscribable<number>
    otherPageElementWidth: Subscribable<number>

    pageIdx: Subscribable<number>
    alignment: Subscribable<WCCSliderAlignment>

    showOtherPages: Subscribable<boolean>
    showControls: Subscribable<boolean>
    showPages: Subscribable<boolean>
    hasInlineControls: Subscribable<boolean>
    hasMultiplePages: Subscribable<boolean>
    hasNextPage: Subscribable<boolean>
    hasPrevPage: Subscribable<boolean>
    ready: Subscribable<boolean>

    selectedItem: Subscribable<WCCSliderItem<T> | undefined>
    activePage: Subscribable<WCCSliderPage<T> | undefined>
    defaultPage: Subscribable<WCCSliderPage<T> | undefined>
    nextPage: Subscribable<WCCSliderPage<T> | undefined>
    prevPage: Subscribable<WCCSliderPage<T> | undefined>

    source: Subscribable<T[]>       
    pages: Subscribable<WCCSliderPage<T>[]>
    nextPages: Subscribable<WCCSliderPage<T>[]> //used in plugins
    prevPages: Subscribable<WCCSliderPage<T>[]> //used in plugins
    allItems: Subscribable<WCCSliderItem<T>[]>
    items: Subscribable<WCCSliderItem<T>[]>

    autoScroll: WCCSliderAutoScrollManager<T>

    constructor(
        @inject(wccModules.componentConfig) private model: WCCSlider<T>,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccSliderLayoutsFactoryKey) layoutsFactory: IWCCSliderLayoutsFactory<T>,
        @inject(wccSliderAnimatorsFactoryKey) animatorsFactory: IWCCSliderAnimatorsFactory<T>,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.templates = this.getTemplates(info.templateNodes);

        const { width: bodyWidth } = new NodeTracker({ node: this.bodyNode });
        this.width = bodyWidth;

        this.alignment = ko.pureComputed(() => this.getAlignment());
        this.source = ko.pureComputed(() => this.getSource());

        this.pages = this.layout.pluck(layout => layout.pages, []);
        this.allItems = this.layout.pluck(layout => layout.items, []);

        this.spaceBetweenPages = this.layout.pluck(layout => layout.spaceBetweenPages, 0);
        this.height = this.layout.pluck(layout => layout.height);

        this.items = this.allItems.filter(item => renderStates.includes(item.state()));

        const otherPagesConfig = this.model.otherPages;
        this.showOtherPages = otherPagesConfig.pluck(config => config.show, false);
        this.otherPageElementSize = otherPagesConfig.pluck(config => config.itemWidth);
        this.otherPageElementWidth = this.layout.pluck(layout => this.getOtherElementWidth(layout), 0);
        
        this.selectedItem = this.items.find(item => item.data() === this.selectedSourceItem());
        this.pageIdx = ko.pureComputed(() => this.getPageIdx());

        this.hasNextPage = this.pageIdx.mapSingle(idx => idx < this.pages().length - 1);
        this.nextPage = this.pageIdx.mapSingle<WCCSliderPage<T> | undefined>(idx => this.pages()[idx + 1]);
        this.nextPages = this.pageIdx.mapSingle(idx => this.pages().slice(idx + 1));

        this.hasPrevPage = this.pageIdx.mapSingle(idx => idx > 0);
        this.prevPage = this.pageIdx.mapSingle<WCCSliderPage<T> | undefined>(idx => this.pages()[idx - 1]);
        this.prevPages = this.pageIdx.mapSingle(idx => this.pages().slice(0, idx));

        this.activePage = this.pageIdx.mapSingle<WCCSliderPage<T> | undefined>(idx => this.pages()[idx]);
        this.defaultPage = ko.pureComputed(() => this.getDefaultPage());

        this.hasMultiplePages = this.pages.is(pages => pages.length > 1);
        this.showControls = ko.pureComputed(() => this.model.showControls() && this.hasMultiplePages());
        this.hasInlineControls = ko.pureComputed(() => this.showControls() && !this.model.showControlsOverContent());

        this.showPages = ko.pureComputed(() => this.model.showPages() && this.hasMultiplePages());
        this.ready = ko.pureComputed<boolean>(() => true);        

        this.autoScroll = effects.register(new WCCSliderAutoScrollManager({
            ...model.autoScroll(),

            activePage: this.activePage,
            defaultPage: this.defaultPage,
            hasMultiplePages: this.hasMultiplePages,
            hasNextPage: this.hasNextPage,
            isMouseInsideSlider: this.isMouseInsideSlider,
            ready: this.ready,
            openNextPage: () => this.openNextPage(),
            selectPage: page => this.selectPage(page)
        }));

        effects.register(layoutConfig => {
            const baseConfig: WCCSliderBaseLayoutConfig<T, WCCSliderItem<T>> = {
                width: this.width,
                source: this.source,
                activePage: this.activePage,
                otherPageElementWidth: this.otherPageElementWidth,
                showOtherPages: this.showOtherPages
            }

            const layout = layoutsFactory.build({
                ...layoutConfig,
                ...baseConfig
            });

            this.layout(layout);

            return () => this.layout(undefined);
        }, [this.model.layout]);

        effects.register(animation => {
            const animator = animatorsFactory.build({
                type: animation,
                width: this.width,
                shift: this.shift,
                alignment: this.alignment,
                pageIdx: this.pageIdx,
                pages: this.pages,
                showOtherPages: this.showOtherPages,
                otherPageElementWidth: this.otherPageElementWidth,
                spaceBetweenPages: this.spaceBetweenPages
            });

            this.animator(animator);

            return () => this.animator(undefined);
        }, [this.model.animation]);        

        effects.register(new WCCSliderResizer({
            element: <HTMLElement>info.element,
            height: this.height,
            trackHeight: this.trackHeight
        }));

        effects.register(item => {
            if (item != undefined)
                this.select(item);
        }, [this.model.selectedItem]);

        effects.register([
            ko.computed(() => this.updateActiveItems()).extend({ deferred: true }),
            ko.computed(() => this.renderNext()).extend({ deferred: true })
        ]);

        const shouldCenter = ko.pureComputed(() => this.model.centerFirstPage() && this.pages().length == 1);
        const shouldAlignLeft = ko.pureComputed(() => !shouldCenter() && this.alignment() == WCCSliderAlignment.left);
        const shouldAlignRight = ko.pureComputed(() => !shouldCenter() && this.alignment() == WCCSliderAlignment.right);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'wcc-slider': true,
                'wcc-slider--with-controls': this.hasInlineControls,
                'wcc-slider--align-left': shouldAlignLeft,
                'wcc-slider--align-right': shouldAlignRight,
                'wcc-slider--align-center': shouldCenter
            },

            'event': {
                'mouseenter': () => this.isMouseInsideSlider(true),
                'mouseleave': () => this.isMouseInsideSlider(false)
            },

            'attr': {
                'dir': 'ltr'
            }
        });
    }

    select(item?: T) {
        if (item != undefined && this.source().includes(item))
            this.selectedSourceItem(item);
        else
            this.selectedSourceItem(undefined);
    }

    selectPage(page: WCCSliderPage<T>) {
        if (page === this.defaultPage()) {
            this.select(undefined);
        } else {
            const item = this.alignment() === WCCSliderAlignment.right ?
                _.last(page.items()) :
                _.first(page.items());

            this.select(item?.data());
        }
    }

    openNextPage() {
        if (this.hasNextPage())
            this.selectPage(<WCCSliderPage<T>>this.nextPage());
    }

    openPrevPage() {
        if (this.hasPrevPage())
            this.selectPage(<WCCSliderPage<T>>this.prevPage());
    }

    private getAlignment() {
        if (settings.isRTL())
            return WCCSliderAlignment.right;

        return this.model.alignment();
    }

    private getSource() {
        if (settings.isRTL())
            return [...this.model.source()].reverse();

        return this.model.source();
    }

    private getTemplates(nodes: Node[]): WCCSliderTemplates {
        const hasSliderItem = nodes.some(node => $(node).is('slider-item'));

        if (hasSliderItem) {
            return {
                item: $(nodes).filter(function (idx, node) { return $(node).is('slider-item') }).contents().toArray() as Node[],
                controlNext: $(nodes).filter(function (idx, node) { return $(node).is('slider-control-next') }).contents().toArray() as Node[],
                controlPrev: $(nodes).filter(function (idx, node) { return $(node).is('slider-control-prev') }).contents().toArray() as Node[]
            }
        } else {
            return {
                item: nodes,
                controlNext: [],
                controlPrev: []
            }
        }
    }

    private getDefaultPage() {
        if (this.alignment() === WCCSliderAlignment.right)
            return _.last(this.pages());

        if (this.alignment() === WCCSliderAlignment.left)
            return _.first(this.pages());
    }

    private getPageIdx() {
        const selectedItem = this.selectedItem();

        if (selectedItem != undefined) {
            const idx = this.pages().findIndex(p => p.contains(selectedItem));

            return idx !== -1 ? idx : 0;
        } else if (this.alignment() === WCCSliderAlignment.right) {
            return this.pages().length - 1;
        } else {
            return 0;
        }
    }

    private getOtherElementWidth(layout: IWCCSliderLayout<T>) {
        const width = layout.itemWidth() * this.otherPageElementSize();
        const maxWidth = this.width() * 0.15;

        return Math.min(width, maxWidth);
    }

    private updateActiveItems() {
        const activePage = this.activePage();

        if (activePage != undefined) {
            const activePageItems = activePage.items();

            this.items().forEach(item => item.isActive(false));
            activePageItems.forEach(item => item.isActive(true));
        }
    }

    private renderNext() {
        const canRender = this.allItems().every(item => item.state() != WCCSliderItemState.pending);
        const itemToRender = this.allItems().find(item => item.state() == WCCSliderItemState.justCreated);

        if (canRender && itemToRender !== undefined)
            itemToRender.state(WCCSliderItemState.pending);
    }
}